import {StyleSheet, View, Text} from 'react-native';
import {Svg, Path} from 'react-native-svg';
import {getIcon} from 'utils/mediafire';
import {colors} from 'config/styles';

export interface FileIconProps {
  id: string;
  type: string;
  name: string;
  large?: boolean;
  size?: 'small' | 'medium' | 'large';
  iconColor?: string;
  viewbox?: string;
  width?: number;
  height?: number;
}

export function FileIcon(props: FileIconProps) {
  const icon = getIcon(props.type, props.name);
  const extension = typeof props?.name === 'string' && props.name.split('.')?.pop();
  const badge = extension ? extension.substring(0, 4).toUpperCase() : 'FILE';
  
  // Determine which size to use
  const size = props.size || (props.large ? 'large' : undefined);
  
  // Check if it's a folder
  const isFolder = icon.type === 'folder';
  
  // Set icon dimensions based on size
  // Folders get larger dimensions since they don't have badges
  const iconDimensions = {
    small: isFolder ? { height: 40, width: 45 } : { height: 30, width: 25 },
    medium: isFolder ? { height: 60, width: 70 } : { height: 35, width: 30 },
    large: isFolder ? { height: 90, width: 105 } : { height: 50, width: 45 }
  };
  
  // Use original dimensions for list view (when size is undefined)
  const defaultDimensions = { height: 35, width: 30 };
  const dimensions = size ? iconDimensions[size] : defaultDimensions;
  
  return (
    <View style={styles.root}>
      <View style={[
        styles.inner, 
        size === 'small' && styles.small,
        size === 'medium' && styles.medium,
        size === 'large' && styles.large
      ]}>
        <View style={[isFolder ? styles.iconFolder : styles.iconFile]}>
          <Svg
            viewBox={props.viewbox ?? isFolder ? '0 0 35 28' : '0 0 35 40'}
            height={props.height ?? dimensions.height}
            width={props.width ?? dimensions.width}>
            {icon.vector.map((d,i) =>
              <Path key={i} d={d} fill={props.iconColor}/>
            )}
          </Svg>
          {!isFolder &&
            <View style={[styles.badge, styles[icon.type]]}>
              <Text style={styles.label} numberOfLines={1}>
                {badge}
              </Text>
            </View>
          }
        </View>
      </View>
    </View>
  );
}

export const styles = StyleSheet.create({
  root: {
    justifyContent: 'center',
    alignContent: 'center',
    alignItems: 'center',
    flexGrow: 1,
  },
  inner: {
    position: 'relative',
    alignItems: 'center',
    justifyContent: 'center',
  },
  small: {
    width: 60,
    height: 60,
  },
  medium: {
    width: 100,
    height: 100,
  },
  large: {
    width: 150,
    height: 150,
  },
  iconFile: {
    marginHorizontal: 14,
  },
  iconFolder: {
    marginHorizontal: 14,
  },
  badge: {
    backgroundColor: colors.neutral.black,
    position: 'absolute',
    left: -2,
    top: 6,
  },
  label: {
    fontSize: 8,
    paddingVertical: 1,
    paddingHorizontal: 2,
    fontWeight: '600',
    color: colors.neutral.white,
  },
  file: {
    backgroundColor: colors.files.text,
  },
  folder: {
    backgroundColor: colors.files.text,
  },
  archive: {
    backgroundColor: colors.files.archive,
  },
  audio: {
    backgroundColor: colors.files.audio,
  },
  video: {
    backgroundColor: colors.files.video,
  },
  image: {
    backgroundColor: colors.files.image,
  },
  spreadsheet: {
    backgroundColor: colors.files.spreadsheet,
  },
  pdf: {
    backgroundColor: colors.files.pdf,
  },
  presentation: {
    backgroundColor: colors.files.presentation,
  },
  document: {
    backgroundColor: colors.files.document,
  },
});
